import { useState, useEffect, FormEvent } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import useApi from 'hooks/useApi';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

interface ToggleState {
  usernamePassword: { enabled: boolean };
  otp: { enabled: boolean };
  entraId: { enabled: boolean; clientId: string; tenantId: string };
}

// interface AuthenticationProps {
//   clientUid: string;
// }

const Authentication = ({ clientUid }: any) => {
  const hasClientEditPermission = useHasPermission(PERMISSIONS.ADD_EDIT_CLIENT);
  
  const [toggles, setToggles] = useState<ToggleState>({
    usernamePassword: { enabled: false },
    otp: { enabled: false },
    entraId: { enabled: false, clientId: '', tenantId: '' }
  });

  const { submitSSODetails, getSSODetails } = useApi();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>(
    'info'
  );

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    getClientSSODetails();
  
  }, [clientUid]);

  const getClientSSODetails = async () => {
    try {
      const response = await getSSODetails(clientUid);
      const { success, data } = response;
      if (success && data) {
        setToggles({
          usernamePassword: { enabled: data.usernamePassword?.enabled ?? false },
          otp: { enabled: data.otp?.enabled ?? false },
          entraId: {
            enabled: data.entraId?.enabled ?? false,
            clientId: data.entraId?.clientId ?? '',
            tenantId: data.entraId?.tenantId ?? ''
          }
        });
      }
    } catch (error) {
      console.error('Error fetching SSO details:', error);
    }
  };

  const toggleField = (field: keyof ToggleState) => {
    setToggles(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        enabled: !prev[field].enabled
      }
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (toggles.entraId.enabled) {
      if (!toggles.entraId.clientId || !toggles.entraId.tenantId) {
        showSnackbar('Client ID and Tenant ID are required.', 'error');
        return;
      }
    }

    const payload = {
      usernamePassword: toggles.usernamePassword,
      otp: toggles.otp,
      entraId: toggles.entraId
    };

    try {
      const { success } = await submitSSODetails(payload, clientUid);
      if (success) {
        showSnackbar('Settings saved successfully!', 'success');
      }
    } catch {
      showSnackbar('Something went wrong', 'error');
    }
  };

  return (
    <Box sx={{ p: 3, mx: 'auto', maxWidth: 500 }}>
      <form onSubmit={handleSubmit}>
        {/* Username & Password */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={500} >Username &amp; Password Login</Typography>
            {toggles.usernamePassword.enabled ? (
              <ToggleOnIcon sx={{ fontSize: 40,color:"secondary.dark" }} onClick={() => toggleField('usernamePassword')}  />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 40,color:'grey.500' }} onClick={() => toggleField('usernamePassword')} color="primary" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Traditional login using a username and password.
          </Typography>
        </Box>

        {/* OTP */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={500}>OTP Login (One‑Time Password)</Typography>
            {toggles.otp.enabled ? (
              <ToggleOnIcon sx={{ fontSize: 40,color:"secondary.dark" }} onClick={() => toggleField('otp')} color="primary" />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 40,color:'grey.500' }} onClick={() => toggleField('otp')}  />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Users receive a single‑use code via SMS or email to authenticate.
          </Typography>
        </Box>

        {/* Microsoft Entra ID */}
        <Box mb={toggles.entraId.enabled ? 2 : 3}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={500}> Entra ID (SSO)</Typography>
            {toggles.entraId.enabled ? (
              <ToggleOnIcon onClick={() => toggleField('entraId')} color="primary" sx={{ fontSize: 40,color:"secondary.dark" }} />
            ) : (
              <ToggleOffIcon onClick={() => toggleField('entraId')} sx={{ fontSize: 40,color:'grey.500' }} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Sign in with Entra ID  using OAuth.
          </Typography>
        </Box>

        {toggles.entraId.enabled && (
          <Box mb={3}>
            <TextField
              label="Tenant ID"
              value={toggles.entraId.tenantId}
              onChange={(e) =>
                setToggles(prev => ({
                  ...prev,
                  entraId: { ...prev.entraId, tenantId: e.target.value }
                }))
              }
              // helperText="Your Azure Active Directory Tenant ID."
              margin="normal"
              fullWidth
            />

            <TextField
              label=" Client ID"
              value={toggles.entraId.clientId}
              onChange={(e) =>
                setToggles(prev => ({
                  ...prev,
                  entraId: { ...prev.entraId, clientId: e.target.value }
                }))
              }
              // helperText="The Application (client) ID from your Microsoft App registration."
              margin="normal"
              fullWidth
            />
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!hasClientEditPermission}
            sx={{bgcolor:"secondary.dark",'&:hover': {
      bgcolor: 'secondary.main'
    }}}>
            Save
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Authentication;
